import React from 'react';
import { formatMinutesToDuration } from '../../utils/myWorkFormatters';

/**
 * EstimatedEffortBars Component
 * Column 2 of the Weekly Work Overview.
 * Displays Estimated Time, Effort Time (from Time Tracking), and independent Remaining Work.
 */
export default function EstimatedEffortBars({
    estimatedMinutes = 0,
    effortMinutes = 0,
    remainingMinutes = 0,
    maxCapacityMinutes = 2400, // 40h reference for percentages
}) {
    // Calculate percentage relative to weekly reference (e.g. 40h or highest value)
    const baseReference = Math.max(maxCapacityMinutes, estimatedMinutes, effortMinutes, remainingMinutes, 60);

    const estPercent = Math.min(Math.round((estimatedMinutes / baseReference) * 100), 100);
    const effortPercent = Math.min(Math.round((effortMinutes / baseReference) * 100), 100);
    const remPercent = Math.min(Math.round((remainingMinutes / baseReference) * 100), 100);

    const formattedEstimated = formatMinutesToDuration(estimatedMinutes);
    const formattedEffort = formatMinutesToDuration(effortMinutes);
    const formattedRemaining = formatMinutesToDuration(remainingMinutes);

    return (
        <div className="flex flex-col lg:pl-8 pt-6 lg:pt-0 pr-0 lg:pr-2">
            <div className="mb-5">
                <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline tracking-tight">
                    Estimated &amp; Effort
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Your time breakdown for the week.
                </p>
            </div>

            <div className="space-y-4 my-auto">
                {/* Estimated Time */}
                <div>
                    <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Estimated Time</span>
                        <span className="font-bold text-slate-900 dark:text-white font-headline font-mono text-sm">
                            {formattedEstimated}
                        </span>
                    </div>
                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${estPercent}%` }}
                        />
                    </div>
                </div>

                {/* Effort Time */}
                <div>
                    <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Effort Time</span>
                        <span className="font-bold text-slate-900 dark:text-white font-headline font-mono text-sm">
                            {formattedEffort}
                        </span>
                    </div>
                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{ width: `${effortPercent}%` }}
                        />
                    </div>
                </div>

                {/* Remaining Work */}
                <div>
                    <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Remaining Work</span>
                        <span className="font-bold text-slate-900 dark:text-white font-headline font-mono text-sm">
                            {formattedRemaining}
                        </span>
                    </div>
                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-amber-400 rounded-full transition-all duration-500"
                            style={{ width: `${remPercent}%` }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
